import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    Input,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Select,
    Text,
    useColorModeValue,
} from "@chakra-ui/react";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { checkErrors, hasError, isValidCEP } from "utils/forms-utils";
import {
    validateCPF,
    validateCNPJ,
    validateEmail,
    formatCEP,
    formatPhoneNumber,
    fetchAddressByCEP,
    ufs, formatDocument,
} from "utils/utils";

const PersonModal = ({ isOpen, onClose, isPersonValid, selectedPerson, onSave }) => {
    const { t } = useTranslation();
    const textColor = useColorModeValue("gray.700", "white");
    const [inputError, setInputError] = useState({});

    const ids = useMemo(
        () => ({
            cpfCnpj: "cpfCnpj",
            name: "name",
            socialName: "socialName",
            surname: "surname",
            issuingAgency: "issuingAgency",
            rg: "rg",
            ufRg: "ufRg",
            cep: "cep",
            logradouro: "logradouro",
            numero: "numero",
            bairro: "bairro",
            municipio: "municipio",
            uf: "uf",
            phone: "phone",
            cellphone: "cellphone",
            email: "email",
        }),
        []
    );

    const maxLengthValidation = (max) => ({
        callback: ({ value }) =>
            value.length > max ? t("system.messages.errors.max-length", { max }) : null,
    });

    const validPhoneNumber = () => ({
        callback: ({ value }) =>
            value && value.length >= 15 || value.length === 0
                ? null
                : t("system.messages.errors.phone-number-invalid"),
    });

    const initFormData = (p) => ({
        [ids.cpfCnpj]: {
            value: p?.cpf_cnpj ? formatDocument(p?.cpf_cnpj) : "",
            validations: [
                { required: true },
                {
                    callback: ({ value }) => {
                        const digits = value.replace(/\D/g, "");
                        if (!value) return null;
                        if (digits.length <= 11) {
                            return validateCPF(value) ? null : t("settings.person.form.invalid-document");
                        } else {
                            return validateCNPJ(value) ? null : t("settings.person.form.invalid-document");
                        }
                    },
                },
            ],
        },
        [ids.name]: {
            value: p?.nome || "",
            validations: [
                { required: true },
                maxLengthValidation(255)
            ],
        },
        [ids.socialName]: {
            value: p?.nome_social || "",
            validations: [maxLengthValidation(255)],
        },
        [ids.surname]: {
            value: p?.apelido || "",
            validations: [maxLengthValidation(255)],
        },
        [ids.issuingAgency]: {
            value: p?.orgao_expedidor_identidade || "",
            validations: [maxLengthValidation(255)],
        },
        [ids.rg]: {
            value: p?.numero_identidade || "",
            validations: [maxLengthValidation(255)],
        },
        [ids.ufRg]: {
            value: p?.id_uf_identidade || "",
            validations: [],
        },
        [ids.cep]: {
            value: p?.endereco_cep || "",
            validations: [
                {
                    callback: ({ value }) =>
                        value.length === 9 && !isValidCEP(value)
                            ? t("system.messages.errors.invalid-format.masked.masculine", {
                                labelKey: t("settings.person.form.cep.label"),
                                mask: "99999-999",
                            })
                            : null,
                },
            ],
        },
        [ids.logradouro]: {
            value: p?.endereco_logradouro || "",
            validations: [maxLengthValidation(255)],
        },
        [ids.numero]: {
            value: p?.endereco_numero || "",
            validations: [maxLengthValidation(255)],
        },
        [ids.bairro]: {
            value: p?.endereco_bairro || "",
            validations: [maxLengthValidation(255)],
        },
        [ids.municipio]: {
            value: p?.endereco_municipio || "",
            validations: [maxLengthValidation(255)],
        },
        [ids.uf]: {
            value: p?.endereco_uf || "",
            validations: [],
        },
        [ids.phone]: {
            value: p?.telefone ? formatPhoneNumber(p?.telefone) : "",
            validations: [validPhoneNumber()],
        },
        [ids.cellphone]: {
            value: p?.celular ? formatPhoneNumber(p?.celular) : "",
            validations: [validPhoneNumber()],
        },
        [ids.email]: {
            value: p?.email || "",
            validations: [
                {
                    callback: ({ value }) =>
                        value && !validateEmail(value)
                            ? t("settings.person.form.invalid-email")
                            : null,
                },
            ],
        },
    });

    const [formData, setFormData] = useState(initFormData());

    useEffect(() => {
        if (isOpen) {
            setFormData(initFormData(selectedPerson));
            setInputError({});
        }
    }, [selectedPerson, isOpen]);

    const maskMap = {
        [ids.cpfCnpj]: formatDocument,
        [ids.cep]: formatCEP,
        [ids.phone]: formatPhoneNumber,
        [ids.cellphone]: formatPhoneNumber,
    };

    const validatorMap = {
        [ids.cpfCnpj]: {
            validate: (v) => {
                const d = v.replace(/\D/g, "");
                return d.length <= 11 ? validateCPF(v) : validateCNPJ(v);
            },
            errorKey: "settings.person.form.invalid-document",
        },
        [ids.email]: {
            validate: (v) => !v || validateEmail(v),
            errorKey: "settings.person.form.invalid-email",
        },
        [ids.cep]: {
            validate: (v) => !v || isValidCEP(v),
            errorKey: "system.messages.errors.invalid-format.masked.masculine",
        },
    };

    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        const masked = maskMap[name] ? maskMap[name](value) : value;

        let updated = {
            ...formData,
            [name]: { ...formData[name], value: masked },
        };

        if (name === ids.cep) {
            if (masked.length === 9) {
                try {
                    const addr = await fetchAddressByCEP(masked.replace("-", ""));
                    if (addr) {
                        updated[ids.logradouro].value = addr.logradouro || "";
                        updated[ids.bairro].value = addr.bairro || "";
                        updated[ids.municipio].value = addr.localidade || "";
                        updated[ids.uf].value = addr.uf || "";
                    }
                    setInputError(prev => ({ ...prev, [ids.cep]: "" }));
                } catch {
                    setInputError(prev => ({ ...prev, [ids.cep]: t("system.messages.errors.invalid-cep") }));
                }
            } else if (masked.length > 0 && masked.length < 9) {
                setInputError(prev => ({ ...prev, [ids.cep]: t("system.messages.errors.invalid-cep") }));
            } else {
                setInputError(prev => ({ ...prev, [ids.cep]: "" }));
            }
        }

        setFormData(updated);

        let errs = checkErrors({ formData: updated, inputError, name });
        const v = validatorMap[name];
        if (v) {
            if (!v.validate(masked)) {
                errs[name] = t(v.errorKey,
                    name === ids.cep
                        ? { labelKey: t("settings.person.form.cep.label"), mask: "99999-999" }
                        : {}
                );
            } else {
                delete errs[name];
            }
        }
        setInputError(errs);
    };

    const toDto = (formData) => ({
        nome: formData[ids.name].value,
        cpf_cnpj: formData[ids.cpfCnpj].value.replace(/\D/g, ""),
        nome_social: formData[ids.socialName].value,
        apelido: formData[ids.surname].value,
        orgao_expedidor_identidade: formData[ids.issuingAgency].value,
        numero_identidade: formData[ids.rg].value,
        id_uf_identidade: formData[ids.ufRg].value || null,
        endereco_cep: formData[ids.cep].value,
        endereco_logradouro: formData[ids.logradouro].value,
        endereco_numero: formData[ids.numero].value,
        endereco_bairro: formData[ids.bairro].value,
        endereco_municipio: formData[ids.municipio].value,
        endereco_uf: formData[ids.uf].value,
        telefone: formData[ids.phone].value,
        celular: formData[ids.cellphone].value,
        email: formData[ids.email].value,
    });

    const toFormData = () => {
        const personData = toDto(formData);
        const data = new FormData();

        data.append("requestData", new Blob([JSON.stringify(personData)], { type: "application/json" }));

        return data;
    };

    const handleSubmit = async () => {
        const errs = checkErrors({ formData, inputError });
        if (Object.keys(errs).length) {
            setInputError(errs);
            return;
        }

        const data = toFormData();

        try {
            await onSave(data);
            onClose();
            setFormData(initFormData());
            setInputError({});
        } catch (error) {
            console.error(error);
        }
    };

    const isFormInvalid = () => {
        return formData && inputError ? hasError({ formData, inputError }) : true;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text color={textColor} fontSize="lg" fontWeight="bold">
                        {isPersonValid
                            ? t("settings.person.edit.title")
                            : t("settings.person.new.title")}
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Grid templateColumns={{ sm: "1fr", md: "repeat(2, 1fr)" }} gap="24px">
                        {/* CPF/CNPJ */}
                        <FormControl isRequired isInvalid={!!inputError[ids.cpfCnpj]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.cpf-cnpj.label")}
                            </FormLabel>
                            <Input
                                name={ids.cpfCnpj}
                                value={formData[ids.cpfCnpj].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.cpf-cnpj.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>
                                {t(inputError[ids.cpfCnpj])}
                            </FormErrorMessage>
                        </FormControl>
                        {/* Nome */}
                        <FormControl isRequired isInvalid={!!inputError[ids.name]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.name.label")}
                            </FormLabel>
                            <Input
                                name={ids.name}
                                value={formData[ids.name].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.name.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>
                                {t(inputError[ids.name])}
                            </FormErrorMessage>
                        </FormControl>
                        {/* Nome social */}
                        <FormControl isInvalid={!!inputError[ids.socialName]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.social-name.label")}
                            </FormLabel>
                            <Input
                                name={ids.socialName}
                                value={formData[ids.socialName].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.social-name.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>{t(inputError[ids.socialName])}</FormErrorMessage>
                        </FormControl>
                        {/* Apelido */}
                        <FormControl isInvalid={!!inputError[ids.surname]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.surname.label")}
                            </FormLabel>
                            <Input
                                name={ids.surname}
                                value={formData[ids.surname].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.surname.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>{t(inputError[ids.surname])}</FormErrorMessage>
                        </FormControl>
                        {/* Órgão expedidor */}
                        <FormControl isInvalid={!!inputError[ids.issuingAgency]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.issuing-agency.label")}
                            </FormLabel>
                            <Input
                                name={ids.issuingAgency}
                                value={formData[ids.issuingAgency].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.issuing-agency.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>{t(inputError[ids.issuingAgency])}</FormErrorMessage>
                        </FormControl>
                        {/* RG */}
                        <FormControl isInvalid={!!inputError[ids.rg]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.rg.label")}
                            </FormLabel>
                            <Input
                                name={ids.rg}
                                value={formData[ids.rg].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.rg.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>{t(inputError[ids.rg])}</FormErrorMessage>
                        </FormControl>
                        {/* UF RG */}
                        <FormControl isInvalid={!!inputError[ids.ufRg]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.uf-rg.label")}
                            </FormLabel>
                            <Select
                                name={ids.ufRg}
                                value={formData[ids.ufRg].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.uf-rg.placeholder")}
                                fontSize="sm"
                            >
                                {ufs.map((u) => (
                                    <option key={u.value} value={u.value}>
                                        {u.value} – {u.label}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{t(inputError[ids.ufRg])}</FormErrorMessage>
                        </FormControl>
                        {/* CEP */}
                        <FormControl isInvalid={!!inputError[ids.cep]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.cep.label")}
                            </FormLabel>
                            <Input
                                name={ids.cep}
                                value={formData[ids.cep].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.cep.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>
                                {t(inputError[ids.cep])}
                            </FormErrorMessage>
                        </FormControl>
                        {/* Logradouro */}
                        <FormControl isInvalid={!!inputError[ids.logradouro]} gridColumn="span 2">
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.street.label")}
                            </FormLabel>
                            <Input
                                name={ids.logradouro}
                                value={formData[ids.logradouro].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.street.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>
                                {t(inputError[ids.logradouro])}
                            </FormErrorMessage>
                        </FormControl>
                        {/* Número */}
                        <FormControl isInvalid={!!inputError[ids.numero]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.number.label")}
                            </FormLabel>
                            <Input
                                name={ids.numero}
                                value={formData[ids.numero].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.number.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>{t(inputError[ids.numero])}</FormErrorMessage>
                        </FormControl>
                        {/* Bairro */}
                        <FormControl isInvalid={!!inputError[ids.bairro]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.neighborhood.label")}
                            </FormLabel>
                            <Input
                                name={ids.bairro}
                                value={formData[ids.bairro].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.neighborhood.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>
                                {t(inputError[ids.bairro])}
                            </FormErrorMessage>
                        </FormControl>
                        {/* Município */}
                        <FormControl isInvalid={!!inputError[ids.municipio]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.city.label")}
                            </FormLabel>
                            <Input
                                name={ids.municipio}
                                value={formData[ids.municipio].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.city.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>
                                {t(inputError[ids.municipio])}
                            </FormErrorMessage>
                        </FormControl>
                        {/* UF */}
                        <FormControl isInvalid={!!inputError[ids.uf]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.state.label")}
                            </FormLabel>
                            <Select
                                name={ids.uf}
                                value={formData[ids.uf].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.state.placeholder")}
                                fontSize="sm"
                            >
                                {ufs.map((u) => (
                                    <option key={u.value} value={u.value}>
                                        {u.value} – {u.label}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>
                                {t(inputError[ids.uf])}
                            </FormErrorMessage>
                        </FormControl>
                        {/* Telefone */}
                        <FormControl isInvalid={!!inputError[ids.phone]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.phone.label")}
                            </FormLabel>
                            <Input
                                name={ids.phone}
                                value={formData[ids.phone].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.phone.placeholder")}
                                fontSize="sm"
                                maxLength={15}
                            />
                            <FormErrorMessage>{t(inputError[ids.phone])}</FormErrorMessage>
                        </FormControl>
                        {/* Celular */}
                        <FormControl isInvalid={!!inputError[ids.cellphone]}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.cellphone.label")}
                            </FormLabel>
                            <Input
                                name={ids.cellphone}
                                value={formData[ids.cellphone].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.cellphone.placeholder")}
                                fontSize="sm"
                                maxLength={15}
                            />
                            <FormErrorMessage>{t(inputError[ids.cellphone])}</FormErrorMessage>
                        </FormControl>
                        {/* Email */}
                        <FormControl isInvalid={!!inputError[ids.email]} gridColumn="span 2">
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.person.form.email.label")}
                            </FormLabel>
                            <Input
                                name={ids.email}
                                value={formData[ids.email].value}
                                onChange={handleInputChange}
                                placeholder={t("settings.person.form.email.placeholder")}
                                fontSize="sm"
                            />
                            <FormErrorMessage>{t(inputError[ids.email])}</FormErrorMessage>
                        </FormControl>
                    </Grid>
                    <Flex justify="space-between" mt={6} mb={4}>
                        <Button
                            variant="no-effects"
                            alignSelf="flex-end"
                            w="125px"
                            bg="gray.100"
                            color="gray.700"
                            fontSize="sm"
                            fontWeight="bold"
                            onClick={onClose}
                        >
                            {t("system.actions.cancel")}
                        </Button>
                        <Button
                            w="125px"
                            variant="dark"
                            fontSize="sm"
                            alignSelf="flex-end"
                            type="submit"
                            _hover={{
                                bg: "grey.500",
                                color: "white"
                            }}
                            isDisabled={isFormInvalid()}
                            onClick={handleSubmit}
                        >
                            {t("system.actions.save")}
                        </Button>
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default PersonModal;
